import React, { useEffect, useState } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { ProjectData, ProjectCategory } from '../../hooks/useProjects';
import { Upload, X, Star } from 'lucide-react';
import { compressImage } from '../../utils/imageUtils';
interface ProjectFormProps {
  initialData?: ProjectData | null;
  onSubmit: (data: ProjectData) => void;
  onCancel: () => void;
}
const CATEGORIES: {
  value: ProjectCategory;
  label: string;
}[] = [{
  value: 'Oficinas corporativas',
  label: 'Oficinas corporativas'
}, {
  value: 'Sucursales bancarias',
  label: 'Sucursales bancarias'
}, {
  value: 'Retail y tiendas',
  label: 'Retail y tiendas'
}, {
  value: 'Educación / institucional',
  label: 'Educación / institucional'
}, {
  value: 'Industrial / especial',
  label: 'Industrial / especial'
}, {
  value: 'Otros',
  label: 'Otros'
}];
export function ProjectForm({
  initialData,
  onSubmit,
  onCancel
}: ProjectFormProps) {
  const [formData, setFormData] = useState<ProjectData>({
    name: '',
    category: 'Oficinas corporativas',
    images: [],
    description: '',
    year: new Date().getFullYear(),
    m2: 0,
    comuna: '',
    featured: false
  });
  const [isProcessing, setIsProcessing] = useState(false);
  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setIsProcessing(true);
      try {
        const newImages: string[] = [];
        const files = Array.from(e.target.files);
        // Limit total images to 5
        const remainingSlots = 5 - formData.images.length;
        const filesToProcess = files.slice(0, remainingSlots);
        for (const file of filesToProcess) {
          const compressed = await compressImage(file);
          newImages.push(compressed);
        }
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, ...newImages]
        }));
      } catch (error) {
        console.error('Error processing images:', error);
        alert('Error al procesar las imágenes. Intenta con archivos más pequeños.');
      } finally {
        setIsProcessing(false);
        // Reset input
        e.target.value = '';
      }
    }
  };
  const removeImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };
  const setAsCover = (index: number) => {
    if (index === 0) return; // Ya es portada
    setFormData(prev => {
      const newImages = [...prev.images];
      const [coverImage] = newImages.splice(index, 1);
      newImages.unshift(coverImage);
      return {
        ...prev,
        images: newImages
      };
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.images.length === 0) {
      alert('Debes subir al menos una imagen para el proyecto.');
      return;
    }
    onSubmit(formData);
  };
  return <form onSubmit={handleSubmit} className="space-y-4" data-id="element-252">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-253">
        <Input label="Nombre del Proyecto" value={formData.name} onChange={e => setFormData({
        ...formData,
        name: e.target.value
      })} placeholder="Ej: Oficinas Corporativas" required data-id="element-254" />
        <Select label="Categoría" value={formData.category} onChange={e => setFormData({
        ...formData,
        category: e.target.value as ProjectCategory
      })} options={CATEGORIES} data-id="element-255" />
      </div>

      <div className="space-y-2" data-id="element-256">
        <label className="block text-sm font-medium text-gray-700" data-id="element-257">
          Galería de Imágenes (Máx. 5)
        </label>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4" data-id="element-258">
          {formData.images.map((img, index) => <div key={index} className="relative aspect-square rounded-lg overflow-hidden border-2 border-gray-200 group hover:border-blue-400 transition-colors" data-id="element-259">
              <img src={img} alt={`Preview ${index}`} className="w-full h-full object-cover" data-id="element-260" />

              {/* Overlay con controles */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center" data-id="element-261">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-2" data-id="element-262">
                  {index !== 0 && <button type="button" onClick={() => setAsCover(index)} className="p-2 bg-white text-blue-600 rounded-full hover:bg-blue-50 transition-colors shadow-lg" title="Marcar como portada" data-id="element-263">
                      <Star className="h-4 w-4" data-id="element-264" />
                    </button>}
                  <button type="button" onClick={() => removeImage(index)} className="p-2 bg-white text-red-600 rounded-full hover:bg-red-50 transition-colors shadow-lg" title="Eliminar" data-id="element-265">
                    <X className="h-4 w-4" data-id="element-266" />
                  </button>
                </div>
              </div>

              {/* Badge de portada */}
              {index === 0 && <div className="absolute top-2 left-2 bg-blue-600 text-white text-[10px] font-bold px-2 py-1 rounded-full flex items-center gap-1 shadow-lg" data-id="element-267">
                  <Star className="h-3 w-3 fill-current" data-id="element-268" />
                  PORTADA
                </div>}
            </div>)}

          {formData.images.length < 5 && <label className={`
              flex flex-col items-center justify-center aspect-square rounded-lg border-2 border-dashed border-gray-300 
              cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors
              ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}
            `} data-id="element-269">
              {isProcessing ? <div className="flex flex-col items-center" data-id="element-270">
                  <div className="h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mb-1" data-id="element-271" />
                  <span className="text-xs text-gray-500" data-id="element-272">Procesando...</span>
                </div> : <>
                  <Upload className="h-6 w-6 text-gray-400 mb-1" data-id="element-273" />
                  <span className="text-xs text-gray-500" data-id="element-274">Subir foto</span>
                </>}
              <input type="file" accept="image/*" multiple className="hidden" onChange={handleImageUpload} disabled={isProcessing} data-id="element-275" />
            </label>}
        </div>
        <p className="text-xs text-gray-500" data-id="element-276">
          Haz clic en el ícono de estrella para marcar una foto como portada. La
          portada se mostrará en la galería principal.
        </p>
      </div>

      <div className="sm:col-span-2" data-id="element-277">
        <Input label="Descripción Corta" value={formData.description} onChange={e => setFormData({
        ...formData,
        description: e.target.value
      })} placeholder="Breve descripción del proyecto..." required data-id="element-278" />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-id="element-279">
        <Input label="Año" type="number" value={formData.year} onChange={e => setFormData({
        ...formData,
        year: parseInt(e.target.value) || 0
      })} required data-id="element-280" />
        <Input label="M2" type="number" value={formData.m2} onChange={e => setFormData({
        ...formData,
        m2: parseInt(e.target.value) || 0
      })} required data-id="element-281" />
        <Input label="Comuna" value={formData.comuna} onChange={e => setFormData({
        ...formData,
        comuna: e.target.value
      })} placeholder="Ej: Las Condes" required data-id="element-282" />
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 mt-6" data-id="element-283">
        <Button type="button" variant="secondary" onClick={onCancel} data-id="element-284">
          Cancelar
        </Button>
        <Button type="submit" disabled={isProcessing} data-id="element-285">
          {initialData ? 'Guardar Cambios' : 'Crear Proyecto'}
        </Button>
      </div>
    </form>;
}